/**
 * Журнал действий по делу (ТЗ FR-48) и восстановление профиля из резервной
 * копии (ТЗ п. 5.1).
 *
 * Обе вещи нужны в одном месте по одной причине: пользуются ими вместе.
 * Сначала смотрят, что случилось с делом и когда, потом откатываются
 * к редакции до злополучной правки.
 *
 * Восстановление доступно юристу: это перезапись всего дела, и делать её
 * помощнику незачем (разграничение прав — раздел 3 ТЗ, проверяется сервером).
 */

import { useEffect, useState } from 'react';
import type { CaseProfile } from '@bd/domain';
import { api, ApiError } from '../../api.js';

interface JournalEntry {
  action: string;
  user_id: string | null;
  revision: number | null;
  stage: string | null;
  comment: string | null;
  created_at: string;
}

interface Backup {
  revision: number;
  createdAt: string;
  expiresAt: string;
}

/** Действия журнала по-русски: код в интерфейсе ничего не объясняет. */
const ACTIONS: Record<string, string> = {
  profile_saved: 'Профиль сохранён',
  generated: 'Сформирован пакет',
  generation_failed: 'Формирование не удалось',
  template_uploaded: 'Загружен шаблон',
  stage_changed: 'Изменена стадия',
  profile_restored: 'Профиль восстановлен из копии',
};

const dateTime = (value: string) => (value ? new Date(value).toLocaleString('ru-RU') : '—');

export function HistoryPanel({
  caseId,
  readOnly,
  onRestored,
}: {
  caseId: string;
  readOnly: boolean;
  /** Восстановленный профиль записывается в карточку тем же путём, что и обычная правка */
  onRestored: (profile: CaseProfile) => Promise<void>;
}) {
  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [backups, setBackups] = useState<Backup[] | null>(null);
  const [journalError, setJournalError] = useState<string | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = () => {
    api
      .journal(caseId)
      .then((r) => setEntries(r.entries as unknown as JournalEntry[]))
      .catch((e) => setJournalError((e as ApiError).message));

    api
      .backups(caseId)
      .then((r) => setBackups(r.backups))
      .catch((e) => setBackupError((e as ApiError).message));
  };

  useEffect(load, [caseId]);

  const restore = async (revision: number) => {
    setBusy(true);
    setMessage(null);
    setBackupError(null);
    try {
      const { profile } = await api.restoreBackup(caseId, revision);
      await onRestored(profile);
      setMessage(`Профиль восстановлен из редакции ${revision}. Проверьте блоки дела перед формированием пакета.`);
      setConfirming(null);
      load();
    } catch (error) {
      setBackupError((error as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="block">
      <h2>История дела</h2>

      {message && <div className="success-box">{message}</div>}

      <h3>Журнал действий</h3>
      <p className="hint">
        Кто и когда изменял дело, формировал пакет и менял стадию. Содержания дела в журнале нет —
        только сами действия.
      </p>

      {journalError && <div className="error-box">Журнал недоступен: {journalError}</div>}

      {entries && entries.length > 0 && (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Когда</th>
              <th>Действие</th>
              <th>Кто</th>
              <th>Редакция</th>
              <th>Примечание</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => (
              <tr key={i}>
                <td>{dateTime(entry.created_at)}</td>
                <td>{ACTIONS[entry.action] ?? entry.action}</td>
                <td>{entry.user_id ?? '—'}</td>
                <td>{entry.revision ?? '—'}</td>
                <td>{entry.comment ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {entries && entries.length === 0 && <p className="hint">По делу пока ничего не происходило.</p>}

      <h3>Резервные копии профиля</h3>
      <p className="hint">
        Копия делается при каждом сохранении и хранится зашифрованной. Восстановление перезаписывает
        дело целиком — то, что введено после выбранной редакции, будет потеряно.
      </p>

      {backupError && <div className="error-box">{backupError}</div>}

      {backups && backups.length > 0 && (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Редакция</th>
              <th>Сохранена</th>
              <th>Хранится до</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {backups.map((backup) => (
              <tr key={backup.revision}>
                <td>{backup.revision}</td>
                <td>{dateTime(backup.createdAt)}</td>
                <td>{dateTime(backup.expiresAt)}</td>
                <td>
                  {confirming === backup.revision ? (
                    <>
                      <span className="warning">Перезаписать дело редакцией {backup.revision}?</span>{' '}
                      <button type="button" className="link-button" disabled={busy} onClick={() => restore(backup.revision)}>
                        {busy ? 'Восстановление…' : 'Да, восстановить'}
                      </button>{' '}
                      <button type="button" className="link-button" onClick={() => setConfirming(null)}>
                        Отмена
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="link-button"
                      disabled={readOnly || busy}
                      title={readOnly ? 'Дело открыто другим пользователем' : undefined}
                      onClick={() => setConfirming(backup.revision)}
                    >
                      Восстановить
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {backups && backups.length === 0 && !backupError && (
        <p className="hint">Копий пока нет: они появляются после первого сохранения дела.</p>
      )}
    </section>
  );
}
